// Deal Pipeline spec §5.5 — TanStack Query hooks for the Deal Detail
// page. Reads + mutations against the deal controllers
// (DealsController, DealWorkflowControllers).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  dealsApi,
  documentRequestsApi,
  intakeChecklistApi,
} from "@/api/deals";
import { dealKeys } from "./useDeals";
import { invalidateActivityFor } from "@/pages/activity/hooks/useActivityByOwner";
import { escalationKeys } from "@/hooks/dealFlow/escalationKeys";
import type {
  AddCustomChecklistItemRequest,
  AttestDocumentRequestRequest,
  CreateDocumentRequestRequest,
  IntakeChecklistItemDto,
  UpdateBrokerFeeAuthorizationRequest,
  UpdateBuyerConsultingFeeAuthorizationRequest,
  UpdateDealEditBundleRequest,
  UpsertChecklistItemRequest,
} from "@/types/deals";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const dealDetailKeys = {
  intakeBundle: (dealId: string) => [...dealKeys.detail(dealId), "intake-bundle"] as const,
  submissionAnswers: (dealId: string) => [...dealKeys.detail(dealId), "submission-answers"] as const,
  editBundle: (dealId: string) => [...dealKeys.detail(dealId), "edit-bundle"] as const,
  checklist: (dealId: string) => [...dealKeys.detail(dealId), "checklist"] as const,
  documentRequests: (dealId: string) =>
    [...dealKeys.detail(dealId), "document-requests"] as const,
  brokerFeeAuthorization: (dealId: string) =>
    [...dealKeys.detail(dealId), "broker-fee-authorization"] as const,
  buyerConsultingFeeAuthorization: (dealId: string) =>
    [...dealKeys.detail(dealId), "buyer-consulting-fee-authorization"] as const,
};

function invalidateDealDetail(qc: ReturnType<typeof useQueryClient>, dealId: string) {
  qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
  qc.invalidateQueries({ queryKey: dealKeys.lists() });
  qc.invalidateQueries({ queryKey: dealKeys.activity(dealId) });
}

// Bug-B fix — the intake-checklist mutation endpoints return a single
// IntakeChecklistItemDto (create/update) or nothing (delete), never the
// full IntakeChecklistDto. Writing that response straight into the
// checklist query cache with setQueryData(key, data) replaced the whole
// cached object with a bare item, so `checklist.data?.items` briefly read
// as undefined until the invalidateDealDetail-triggered refetch resolved —
// rendering a transient "0/0 required items complete" / "no items" state.
// These helpers merge into the cached item array instead of overwriting it.
type CachedChecklist = { items: IntakeChecklistItemDto[] } & Record<string, unknown>;

function isCachedChecklist(value: unknown): value is CachedChecklist {
  return !!value && typeof value === "object" && Array.isArray((value as CachedChecklist).items);
}

function upsertCachedChecklistItem(
  qc: ReturnType<typeof useQueryClient>,
  dealId: string,
  item: IntakeChecklistItemDto,
) {
  qc.setQueryData(dealDetailKeys.checklist(dealId), (old: unknown) => {
    if (!isCachedChecklist(old)) return old;
    const exists = old.items.some((it) => it.id === item.id);
    return {
      ...old,
      items: exists
        ? old.items.map((it) => (it.id === item.id ? item : it))
        : [...old.items, item],
    };
  });
}

function removeCachedChecklistItem(
  qc: ReturnType<typeof useQueryClient>,
  dealId: string,
  itemId: string,
) {
  qc.setQueryData(dealDetailKeys.checklist(dealId), (old: unknown) => {
    if (!isCachedChecklist(old)) return old;
    return { ...old, items: old.items.filter((it) => it.id !== itemId) };
  });
}

// ---------------------------------------------------------------------------
// Stage transitions / certification / assignment history (reads)
// ---------------------------------------------------------------------------

export function useStageTransitions(dealId: string | undefined) {
  return useQuery({
    queryKey: dealKeys.stageTransitions(dealId ?? ""),
    queryFn: () => dealsApi.stageTransitions(dealId!),
    enabled: !!dealId,
  });
}

// Deal Submission spec §6 — qualitative intake profile +
// multi-year financial history landed by the import engine.
export function useDealIntakeBundle(dealId: string | undefined) {
  return useQuery({
    queryKey: dealDetailKeys.intakeBundle(dealId ?? ""),
    queryFn: () => dealsApi.intakeBundle(dealId!),
    enabled: !!dealId,
  });
}

export function useDealSubmissionAnswers(dealId: string | undefined) {
  return useQuery({
    queryKey: dealDetailKeys.submissionAnswers(dealId ?? ""),
    queryFn: () => dealsApi.submissionAnswers(dealId!),
    enabled: !!dealId,
  });
}

export function useDealEditBundle(dealId: string | undefined) {
  return useQuery({
    queryKey: dealDetailKeys.editBundle(dealId ?? ""),
    queryFn: () => dealsApi.editBundle(dealId!),
    enabled: !!dealId,
  });
}

export function useUpdateDealEditBundle(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateDealEditBundleRequest) =>
      dealsApi.updateEditBundle(dealId, body),
    onSuccess: (bundle) => {
      qc.setQueryData(dealKeys.detail(dealId), bundle.deal);
      qc.setQueryData(dealDetailKeys.editBundle(dealId), bundle);
      qc.invalidateQueries({ queryKey: dealDetailKeys.submissionAnswers(dealId) });
      invalidateDealDetail(qc, dealId);
      // A deal type change writes an activity row (Owner Recapitalization
      // Loan, surface U4); the Activity tab reads the cross-module feed.
      invalidateActivityFor(qc, "deal", dealId);
    },
  });
}

export function useAssignmentHistory(dealId: string | undefined) {
  return useQuery({
    queryKey: dealKeys.assignmentHistory(dealId ?? ""),
    queryFn: () => dealsApi.assignmentHistory(dealId!),
    enabled: !!dealId,
  });
}

// ---------------------------------------------------------------------------
// Broker Fee Authorization
// ---------------------------------------------------------------------------

export function useBrokerFeeAuthorization(dealId: string | undefined) {
  return useQuery({
    queryKey: dealDetailKeys.brokerFeeAuthorization(dealId ?? ""),
    queryFn: () => dealsApi.brokerFeeAuthorization(dealId!),
    enabled: !!dealId,
  });
}

export function useUpdateBrokerFeeAuthorization(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBrokerFeeAuthorizationRequest) =>
      dealsApi.updateBrokerFeeAuthorization(dealId, body),
    onSuccess: (dto) => {
      qc.setQueryData(dealDetailKeys.brokerFeeAuthorization(dealId), dto);
      qc.invalidateQueries({ queryKey: dealDetailKeys.brokerFeeAuthorization(dealId) });
      invalidateDealDetail(qc, dealId);
    },
  });
}

export function useConfirmBrokerFeeAuthorization(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dealsApi.confirmBrokerFeeAuthorization(dealId),
    onSuccess: (dto) => {
      qc.setQueryData(dealDetailKeys.brokerFeeAuthorization(dealId), dto);
      qc.invalidateQueries({ queryKey: dealDetailKeys.brokerFeeAuthorization(dealId) });
      invalidateDealDetail(qc, dealId);
    },
  });
}

// ---------------------------------------------------------------------------
// Buyer Consulting Fee Authorization
// ---------------------------------------------------------------------------
// Both mutations return the fresh DTO, so each seeds the read cache, then
// invalidates its own key and the deal detail — the three-step shape the
// broker-fee mutations above use. Saving re-opens verification server-side,
// so the card must re-read rather than assume its own optimistic answer.

export function useUpdateBuyerConsultingFeeAuthorization(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBuyerConsultingFeeAuthorizationRequest) =>
      dealsApi.updateBuyerConsultingFeeAuthorization(dealId, body),
    onSuccess: (dto) => {
      qc.setQueryData(dealDetailKeys.buyerConsultingFeeAuthorization(dealId), dto);
      qc.invalidateQueries({
        queryKey: dealDetailKeys.buyerConsultingFeeAuthorization(dealId),
      });
      invalidateDealDetail(qc, dealId);
    },
  });
}

export function useConfirmBuyerConsultingFeeAuthorization(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dealsApi.confirmBuyerConsultingFeeAuthorization(dealId),
    onSuccess: (dto) => {
      qc.setQueryData(dealDetailKeys.buyerConsultingFeeAuthorization(dealId), dto);
      qc.invalidateQueries({
        queryKey: dealDetailKeys.buyerConsultingFeeAuthorization(dealId),
      });
      invalidateDealDetail(qc, dealId);
    },
  });
}

// ---------------------------------------------------------------------------
// Intake checklist
// ---------------------------------------------------------------------------

export function useIntakeChecklist(dealId: string | undefined) {
  return useQuery({
    queryKey: dealDetailKeys.checklist(dealId ?? ""),
    queryFn: () => intakeChecklistApi.get(dealId!),
    enabled: !!dealId,
  });
}

export function useUpsertChecklistItem(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { itemId: string; body: UpsertChecklistItemRequest }) =>
      intakeChecklistApi.upsertItem(dealId, vars.itemId, vars.body),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: dealDetailKeys.checklist(dealId) });
      const prev = qc.getQueryData(dealDetailKeys.checklist(dealId));
      qc.setQueryData(dealDetailKeys.checklist(dealId), (old: unknown) => {
        if (!old || typeof old !== "object" || !("items" in old)) return old;
        const checklist = old as { items: Array<{ id: string; status: string; notes: string | null; assignedTo: string | null }> };
        return {
          ...checklist,
          items: checklist.items.map((it) =>
            it.id === vars.itemId
              ? {
                  ...it,
                  status: vars.body.status,
                  notes: vars.body.notes ?? it.notes,
                  assignedTo: vars.body.assignedTo ?? it.assignedTo,
                }
              : it,
          ),
        };
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev !== undefined) {
        qc.setQueryData(dealDetailKeys.checklist(dealId), context.prev);
      }
    },
    onSuccess: (data) => {
      upsertCachedChecklistItem(qc, dealId, data);
      invalidateDealDetail(qc, dealId);
    },
  });
}

export function useAddCustomChecklistItem(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddCustomChecklistItemRequest) =>
      intakeChecklistApi.addItem(dealId, body),
    onSuccess: (data) => {
      upsertCachedChecklistItem(qc, dealId, data);
      invalidateDealDetail(qc, dealId);
    },
  });
}

export function useDeleteChecklistItem(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => intakeChecklistApi.deleteItem(dealId, itemId),
    onSuccess: (_data, itemId) => {
      removeCachedChecklistItem(qc, dealId, itemId);
      invalidateDealDetail(qc, dealId);
    },
  });
}

// TrackedItemRow (Deal Pipeline spec Appendix A) — escalate / notes-update
// for intake items.
export function useEscalateChecklistItem(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { itemId: string; reason: string }) =>
      intakeChecklistApi.escalate(dealId, vars.itemId, vars.reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealDetailKeys.checklist(dealId) });
      invalidateDealDetail(qc, dealId);
      // A new EscalationRecord exists — refresh the Escalations queue.
      qc.invalidateQueries({ queryKey: escalationKeys.all });
    },
  });
}

export function useUpdateChecklistItemNotes(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { itemId: string; notes: string }) =>
      intakeChecklistApi.updateNotes(dealId, vars.itemId, vars.notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealDetailKeys.checklist(dealId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Document requests
// ---------------------------------------------------------------------------

export function useDocumentRequests(dealId: string | undefined) {
  return useQuery({
    queryKey: dealDetailKeys.documentRequests(dealId ?? ""),
    queryFn: () => documentRequestsApi.list(dealId!),
    enabled: !!dealId,
  });
}

export function useCreateDocumentRequest(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDocumentRequestRequest) =>
      documentRequestsApi.create(dealId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealDetailKeys.documentRequests(dealId) });
      invalidateDealDetail(qc, dealId);
    },
  });
}

// Deal Pipeline spec §6.4 — drives the per-row attestation popup.
export function useAttestDocumentRequest(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: AttestDocumentRequestRequest }) =>
      documentRequestsApi.attest(vars.id, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealDetailKeys.documentRequests(dealId) });
      invalidateDealDetail(qc, dealId);
    },
  });
}

// TrackedItemRow (Deal Pipeline spec Appendix A) — escalate / notes-update
// for document requests.
export function useEscalateDocumentRequest(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      documentRequestsApi.escalate(vars.id, vars.reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealDetailKeys.documentRequests(dealId) });
      invalidateDealDetail(qc, dealId);
      // A new EscalationRecord exists — refresh the Escalations queue.
      qc.invalidateQueries({ queryKey: escalationKeys.all });
    },
  });
}

export function useUpdateDocumentRequestNotes(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; notes: string }) =>
      documentRequestsApi.updateNotes(vars.id, vars.notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealDetailKeys.documentRequests(dealId) });
    },
  });
}
